<!-- src/pages/ShoppingListDetailView.vue -->
<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_SHOPPING_LIST } from '../graphql/queries'
import { UPDATE_SHOPPING_ITEM, DELETE_SHOPPING_ITEM } from '../graphql/mutations'
import { showError, showSuccess } from '../utils/errorHandling'
import dayjs from 'dayjs'
import EditShoppingItemModal from '../components/shopping-item/EditShoppingItemModal.vue'
import CreateShoppingItemForm from '../components/shopping-list/CreateShoppingItemForm.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'

const route = useRoute()
const router = useRouter()

// State
const shoppingListId = computed(() => route.params.id as string)
const showEditModal = ref(false)
const showDeleteDialog = ref(false)
const selectedItem = ref<any>(null)
const showCreateForm = ref(false)
const activeTab = ref<'overview' | 'items' | 'progress'>('items')

// GraphQL
const { result: shoppingListResult, loading, refetch, error } = useQuery(
  GET_SHOPPING_LIST,
  { id: shoppingListId.value },
  { errorPolicy: 'all' }
)

// Mutations
const { mutate: updateShoppingItem, loading: updatingItem } = useMutation(UPDATE_SHOPPING_ITEM)
const { mutate: deleteShoppingItem, loading: deletingItem } = useMutation(DELETE_SHOPPING_ITEM)

// Computed properties
const shoppingList = computed(() => shoppingListResult.value?.shoppingList)
const items = computed(() => shoppingList.value?.items || [])

// Local copy of items — updated optimistically on toggle/drag, synced from server otherwise
const localItems = ref<any[]>([])
const isToggling = ref(false)

// Drag-and-drop state
const isDragging = ref(false)
const draggedItem = ref<any>(null)
const dragOverCategory = ref<string | null>(null)
const dragOverIndex = ref<number>(-1)

watch(items, (serverItems) => {
  if (!isToggling.value && !isDragging.value) {
    localItems.value = [...serverItems]
  }
}, { immediate: true })

// Group items by completion status
const completedItems = computed(() => localItems.value.filter((item: any) => item.is_bought))
// const pendingItems = computed(() => localItems.value.filter((item: any) => !item.is_bought))

// Group items by category
interface ItemCategoryGroup {
  name: string
  items: any[]
  completed: number
  total: number
}

const groupedItemsByCategory = computed(() => {
  const grouped = localItems.value.reduce((groups: Record<string, ItemCategoryGroup>, item: any) => {
    const category = item.type || 'Other'
    if (!groups[category]) {
      groups[category] = {
        name: category,
        items: [],
        completed: 0,
        total: 0
      }
    }
    groups[category].items.push(item)
    groups[category].total++
    if (item.is_bought) {
      groups[category].completed++
    }
    return groups
  }, {} as Record<string, ItemCategoryGroup>)
  // Sort items within each category by position, then created_at
  Object.values(grouped).forEach((cat: any) => {
    cat.items.sort((a: any, b: any) => {
      const posA = a.position ?? 0
      const posB = b.position ?? 0
      if (posA !== posB) return posA - posB
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    })
  })
  return grouped
})

// Progress calculations
const totalItems = computed(() => localItems.value.length)
const completedCount = computed(() => completedItems.value.length)
const progressPercentage = computed(() =>
  totalItems.value > 0 ? Math.round((completedCount.value / totalItems.value) * 100) : 0
)

const totalPrice = computed(() => localItems.value.reduce((sum: number, item: any) => sum + (item.price || 0), 0))
const completedPrice = computed(() => completedItems.value.reduce((sum: number, item: any) => sum + (item.price || 0), 0))

// Methods
const toggleItemStatus = async (item: any) => {
  // Optimistic update — flip is_bought in place without reordering
  const idx = localItems.value.findIndex((i: any) => i.id === item.id)
  if (idx !== -1) {
    localItems.value = localItems.value.map((i: any) =>
      i.id === item.id ? { ...i, is_bought: !item.is_bought } : i
    )
  }

  isToggling.value = true
  try {
    await updateShoppingItem({
      id: item.id,
      list_id: item.list_id,
      name: item.name,
      ingredient_id: item.ingredient_id,
      quantity: item.quantity,
      unit: item.unit,
      price: item.price,
      type: item.type,
      is_bought: !item.is_bought,
    })
  } catch (error) {
    // Roll back optimistic update on failure
    if (idx !== -1) {
      localItems.value = localItems.value.map((i: any) =>
        i.id === item.id ? { ...i, is_bought: item.is_bought } : i
      )
    }
    showError(error, 'update item status')
  } finally {
    isToggling.value = false
  }
}

const editItem = (item: any) => {
  selectedItem.value = item
  showEditModal.value = true
}

const deleteItem = (item: any) => {
  selectedItem.value = item
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!selectedItem.value) return

  try {
    // Save current scroll position
    const scrollY = window.scrollY || window.pageYOffset
    const scrollX = window.scrollX || window.pageXOffset

    await deleteShoppingItem({ id: selectedItem.value.id })
    showSuccess('Item deleted successfully!')
    showDeleteDialog.value = false
    selectedItem.value = null

    // Await the refetch to complete
    await refetch()

    // Restore scroll position after DOM has updated
    requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY)
    })
  } catch (error) {
    showError(error, 'delete item')
  }
}

const cancelDelete = () => {
  showDeleteDialog.value = false
  selectedItem.value = null
}

const onItemUpdated = async () => {
  // Save current scroll position
  const scrollY = window.scrollY || window.pageYOffset
  const scrollX = window.scrollX || window.pageXOffset

  showEditModal.value = false
  selectedItem.value = null

  // Await the refetch to complete
  await refetch()

  // Restore scroll position after DOM has updated
  requestAnimationFrame(() => {
    window.scrollTo(scrollX, scrollY)
  })
}

const onItemCreated = async () => {
  // Save current scroll position
  const scrollY = window.scrollY || window.pageYOffset
  const scrollX = window.scrollX || window.pageXOffset

  showCreateForm.value = false

  // Await the refetch to complete
  await refetch()

  // Restore scroll position after DOM has updated
  requestAnimationFrame(() => {
    window.scrollTo(scrollX, scrollY)
  })
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'bg-primary-100 text-primary-800 border-primary-200'
    case 'completed':
      return 'bg-accent-100 text-accent-800 border-accent-200'
    case 'cancelled':
      return 'bg-red-100 text-red-800 border-red-200'
    default:
      return 'bg-secondary-100 text-secondary-800 border-secondary-200'
  }
}

const getProgressColor = () => {
  if (progressPercentage.value === 100) return 'bg-green-500'
  if (progressPercentage.value >= 75) return 'bg-primary-500'
  if (progressPercentage.value >= 50) return 'bg-accent-500'
  if (progressPercentage.value >= 25) return 'bg-secondary-500'
  return 'bg-gray-400'
}

const formatItemType = (type: string) => {
  if (!type) return ''
  return type.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()
}

// ── Drag-and-drop handlers ────────────────────────────────────────────────

const onDragStart = (event: DragEvent, item: any) => {
  draggedItem.value = item
  isDragging.value = true
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move'
  }
}

const onDragEnd = () => {
  // Clean up if the user drops outside any valid target
  isDragging.value = false
  draggedItem.value = null
  dragOverCategory.value = null
  dragOverIndex.value = -1
}

const onDragOverItem = (event: DragEvent, categoryName: string, index: number) => {
  event.preventDefault()
  event.stopPropagation()
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  dragOverCategory.value = categoryName
  // Insert before or after based on cursor position within the element
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  dragOverIndex.value = event.clientY < rect.top + rect.height / 2 ? index : index + 1
}

const onDragOverCategory = (event: DragEvent, categoryName: string, itemCount: number) => {
  event.preventDefault()
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  // Only update when entering a new category to avoid flickering from child events
  if (dragOverCategory.value !== categoryName) {
    dragOverCategory.value = categoryName
    dragOverIndex.value = itemCount
  }
}

const onDragLeave = (event: DragEvent) => {
  // Only clear when the cursor truly leaves the category container (not a child element)
  const related = event.relatedTarget as HTMLElement | null
  if (!related || !(event.currentTarget as HTMLElement).contains(related)) {
    dragOverCategory.value = null
    dragOverIndex.value = -1
  }
}

const onDrop = async (event: DragEvent, targetCategory: string) => {
  event.preventDefault()

  const dropIndex = dragOverIndex.value
  const item = draggedItem.value

  dragOverCategory.value = null
  dragOverIndex.value = -1
  draggedItem.value = null
  isDragging.value = false

  if (!item) return
  const sameCategory = item.type === targetCategory
  if (sameCategory && dropIndex === -1) return

  // Build the new ordered list for the target category
  const targetItems: any[] = (groupedItemsByCategory.value[targetCategory]?.items || [])
    .filter((i: any) => i.id !== item.id)

  const insertAt = dropIndex === -1 ? targetItems.length : Math.min(dropIndex, targetItems.length)
  targetItems.splice(insertAt, 0, { ...item, type: targetCategory, position: insertAt })

  // Optimistic update — no waiting for API
  localItems.value = localItems.value
    .filter((i: any) => i.id !== item.id)
    .concat({ ...item, type: targetCategory, position: insertAt })

  // Re-index siblings so sort stays stable
  const updatedIds = new Set(targetItems.map((i: any) => i.id))
  localItems.value = localItems.value.map((i: any) => {
    if (!updatedIds.has(i.id) || i.id === item.id) return i
    const newPos = targetItems.findIndex((t: any) => t.id === i.id)
    return newPos !== -1 ? { ...i, position: newPos } : i
  })

  // Fire API updates in the background
  const updates: Promise<any>[] = [
    updateShoppingItem({
      id: item.id,
      list_id: item.list_id,
      name: item.name,
      ingredient_id: item.ingredient_id,
      quantity: item.quantity,
      unit: item.unit,
      price: item.price,
      type: targetCategory,
      is_bought: item.is_bought,
      position: insertAt,
    }),
    ...targetItems
      .map((i: any, idx: number) => {
        if (i.id === item.id || i.position === idx) return null
        return updateShoppingItem({
          id: i.id,
          list_id: i.list_id,
          name: i.name,
          ingredient_id: i.ingredient_id,
          quantity: i.quantity,
          unit: i.unit,
          price: i.price,
          type: i.type,
          is_bought: i.is_bought,
          position: idx,
        })
      })
      .filter(Boolean) as Promise<any>[],
  ]

  Promise.all(updates).catch((err) => showError(err, 'move item'))
}

// Handle 404/error cases
onMounted(() => {
  if (error.value) {
    showError('Shopping list not found', 'load shopping list')
    router.push('/shopping-lists')
  }
})


</script>

<template>
  <div class="page-enter page-container">
    <div class="space-y-4">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
        <p class="mt-3 text-gray-500 text-sm">Loading shopping list...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error || !shoppingList" class="text-center py-8">
        <div class="text-4xl mb-3">😕</div>
        <h3 class="text-lg font-semibold text-gray-700 mb-2">Shopping List Not Found</h3>
        <p class="text-gray-500 mb-4 text-sm">The shopping list you're looking for doesn't exist or has been deleted.
        </p>
        <button @click="router.push('/shopping-lists')"
          class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors focus:ring-2 focus:ring-primary-100">
          Back to Shopping Lists
        </button>
      </div>

      <!-- Shopping List Content -->
      <div v-else>
        <!-- Header -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div class="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-3 mb-1">
                <button @click="router.push('/shopping-lists')"
                  class="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                  </svg>
                </button>
                <h1 class="text-2xl font-bold text-gray-900 truncate">{{ shoppingList.title }}</h1>
                <span
                  :class="`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(shoppingList.status)}`">
                  {{ shoppingList.status }}
                </span>
              </div>
              <p v-if="shoppingList.description" class="text-gray-600 text-sm mb-2">{{ shoppingList.description }}</p>
              <div class="flex items-center gap-3 text-xs text-gray-500">
                <div class="flex items-center gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Created {{ dayjs(shoppingList.created_at).format('MMM D, YYYY') }}
                </div>
                <div v-if="shoppingList.updated_at !== shoppingList.created_at" class="flex items-center gap-1">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                    </path>
                  </svg>
                  Updated {{ dayjs(shoppingList.updated_at).format('MMM D, YYYY') }}
                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="flex gap-2">
              <button @click="showCreateForm = !showCreateForm"
                class="inline-flex items-center px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors focus:ring-2 focus:ring-primary-100 text-sm">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6">
                  </path>
                </svg>
                Add Item
              </button>
            </div>
          </div>

          <!-- Progress Overview -->
          <div class="mt-4 pt-4 border-t border-gray-100">
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
              <!-- Progress Bar -->
              <div class="md:col-span-2">
                <div class="flex items-center justify-between mb-1">
                  <span class="text-sm font-medium text-gray-700">Shopping Progress</span>
                  <span class="text-sm font-semibold text-gray-900">{{ progressPercentage }}%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div :class="`h-2 rounded-full transition-all duration-500 ${getProgressColor()}`"
                    :style="{ width: `${progressPercentage}%` }"></div>
                </div>
                <p class="text-xs text-gray-600 mt-1">
                  {{ completedCount }} of {{ totalItems }} items completed
                </p>
              </div>

              <!-- Stats -->
              <div class="text-center p-3 bg-primary-50 rounded-lg border border-primary-100">
                <div class="text-lg font-bold text-primary-600 mb-1">{{ totalItems }}</div>
                <div class="text-xs font-medium text-primary-600 uppercase tracking-wide">Total Items</div>
              </div>
              <div class="text-center p-3 bg-accent-50 rounded-lg border border-accent-100">
                <div class="text-lg font-bold text-accent-600 mb-1">€{{ totalPrice.toFixed(2) }}</div>
                <div class="text-xs font-medium text-accent-600 uppercase tracking-wide">Total Price</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Create Item Form -->
        <div v-if="showCreateForm" class="bg-white rounded-lg shadow-sm border border-gray-200">
          <CreateShoppingItemForm :shopping-list-id="shoppingListId" :all-ingredients="[]" @item-created="onItemCreated"
            @clear-selection="showCreateForm = false" />
        </div>

        <!-- Tab Navigation -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 mt-4">
          <div class="border-b border-gray-200">
            <nav class="flex space-x-6 px-4" aria-label="Tabs">
              <button @click="activeTab = 'items'" :class="[
                'py-3 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'items'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]">
                Shopping Items ({{ totalItems }})
              </button>
              <button @click="activeTab = 'progress'" :class="[
                'py-3 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'progress'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]">
                Progress Overview
              </button>
            </nav>
          </div>

          <!-- Tab Content -->
          <div class="p-4">
            <!-- Items Tab -->
            <div v-if="activeTab === 'items'">
              <!-- Empty State -->
              <div v-if="totalItems === 0" class="text-center py-8">
                <div class="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <div class="text-2xl">🛒</div>
                </div>
                <h3 class="text-lg font-semibold text-gray-700 mb-2">No items yet</h3>
                <p class="text-gray-500 mb-4 text-sm">Start adding items to your shopping list</p>
                <button @click="showCreateForm = true"
                  class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors focus:ring-2 focus:ring-primary-100">
                  Add First Item
                </button>
              </div>

              <!-- Items by Category -->
              <div v-else class="space-y-6">
                <div v-for="(categoryData, categoryName) in groupedItemsByCategory" :key="categoryName"
                  class="rounded-lg p-3">
                  <!-- Category Header -->
                  <div class="flex items-center justify-between pb-2 border-b border-gray-200">
                    <h3 class="text-base font-semibold text-gray-900">{{ formatItemType(String(categoryName)) }}</h3>
                    <span class="text-xs text-gray-600">
                      {{ categoryData.completed }}/{{ categoryData.total }} completed
                    </span>
                  </div>

                  <!-- Category Items -->
                  <div class="mt-2"
                    @dragover="onDragOverCategory($event, String(categoryName), categoryData.items.length)"
                    @dragleave="onDragLeave"
                    @drop="onDrop($event, String(categoryName))">

                    <template v-for="(item, idx) in categoryData.items" :key="item.id">
                      <!-- Drop indicator line before this item -->
                      <div v-if="dragOverCategory === String(categoryName) && dragOverIndex === idx"
                        class="h-0.5 bg-primary-400 rounded-full mx-1 my-1 transition-all" />

                      <div class="flex items-center gap-2 p-3 mb-1 bg-gray-50 rounded-lg border border-gray-100 transition-all select-none"
                        :class="[
                          draggedItem?.id === item.id ? 'opacity-40' : 'hover:border-gray-200',
                          isDragging ? 'cursor-grabbing' : 'cursor-grab'
                        ]"
                        draggable="true"
                        @dragstart="onDragStart($event, item)"
                        @dragend="onDragEnd"
                        @dragover="onDragOverItem($event, String(categoryName), idx)">

                        <!-- Drag handle -->
                        <svg class="w-3.5 h-3.5 text-gray-300 flex-shrink-0" fill="currentColor" viewBox="0 0 16 16">
                          <circle cx="5" cy="4" r="1.2"/><circle cx="5" cy="8" r="1.2"/><circle cx="5" cy="12" r="1.2"/>
                          <circle cx="11" cy="4" r="1.2"/><circle cx="11" cy="8" r="1.2"/><circle cx="11" cy="12" r="1.2"/>
                        </svg>

                        <!-- Checkbox -->
                        <button @click.stop="toggleItemStatus(item)" :disabled="updatingItem"
                          class="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200"
                          :class="item.is_bought
                            ? 'bg-green-500 border-green-500 text-white'
                            : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50'"
                          :aria-label="item.is_bought ? 'Mark as pending' : 'Mark as completed'">
                          <svg v-if="item.is_bought" class="w-3 h-3" fill="none" stroke="currentColor"
                            viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                          </svg>
                        </button>

                        <!-- Item Content -->
                        <div class="flex-1 min-w-0">
                          <div class="flex items-center justify-between">
                            <div class="flex-1 min-w-0">
                              <h4 class="text-sm font-medium transition-all duration-200"
                                :class="item.is_bought ? 'line-through text-gray-500' : 'text-gray-900'">
                                {{ item.name || 'Unnamed Item' }}
                              </h4>
                              <div class="flex flex-wrap items-center gap-2 text-xs mt-1 transition-all duration-200"
                                :class="item.is_bought ? 'text-gray-400' : 'text-gray-600'">
                                <span v-if="item.quantity">{{ item.quantity }} {{ item.unit || '' }}</span>
                                <span v-if="item.price"
                                  class="inline-flex items-center px-2 py-0.5 bg-accent-50 text-accent-700 rounded-full text-xs font-medium">
                                  €{{ item.price.toFixed(2) }}
                                </span>
                              </div>
                            </div>

                            <!-- Actions -->
                            <div class="flex items-center gap-1 ml-3">
                              <button @click.stop="editItem(item)"
                                class="p-1.5 text-gray-400 hover:text-primary-600 rounded transition-colors"
                                aria-label="Edit item">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button @click.stop="deleteItem(item)"
                                class="p-1.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                                aria-label="Delete item">
                                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>

                    <!-- Drop indicator after the last item -->
                    <div v-if="dragOverCategory === String(categoryName) && dragOverIndex === categoryData.items.length"
                      class="h-0.5 bg-primary-400 rounded-full mx-1 my-1 transition-all" />

                  </div>
                </div>
              </div>
            </div>

          <!-- Progress Tab -->
          <div v-else-if="activeTab === 'progress'" class="space-y-6">
            <!-- Overall Progress -->
            <div class="bg-gradient-to-r from-primary-50 to-accent-50 rounded-lg p-4 border border-primary-100">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Shopping Progress</h3>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="text-center">
                  <div class="text-2xl font-bold text-primary-600 mb-1">{{ completedCount }}</div>
                  <div class="text-xs font-medium text-gray-600">Completed Items</div>
                </div>
                <div class="text-center">
                  <div class="text-2xl font-bold text-secondary-600 mb-1">{{ totalItems - completedCount }}</div>
                  <div class="text-xs font-medium text-gray-600">Remaining Items</div>
                </div>
                <div class="text-center">
                  <div class="text-2xl font-bold text-accent-600 mb-1">{{ progressPercentage }}%</div>
                  <div class="text-xs font-medium text-gray-600">Progress</div>
                </div>
              </div>
            </div>

            <!-- Price Overview -->
            <div v-if="totalPrice > 0" class="bg-white rounded-lg p-4 border border-gray-200">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Price Overview</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div class="text-xl font-bold text-accent-600 mb-1">€{{ completedPrice.toFixed(2) }}</div>
                  <div class="text-sm text-gray-600">Spent so far</div>
                  <div class="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div class="bg-accent-500 h-2 rounded-full transition-all duration-300"
                      :style="{ width: `${totalPrice > 0 ? (completedPrice / totalPrice) * 100 : 0}%` }"></div>
                  </div>
                </div>
                <div>
                  <div class="text-xl font-bold text-gray-600 mb-1">€{{ (totalPrice - completedPrice).toFixed(2) }}
                  </div>
                  <div class="text-sm text-gray-600">Remaining budget</div>
                  <div class="text-xs text-gray-500 mt-2">
                    Total budget: €{{ totalPrice.toFixed(2) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Category Breakdown -->
            <div v-if="Object.keys(groupedItemsByCategory).length > 0"
              class="bg-white rounded-lg p-4 border border-gray-200">
              <h3 class="text-lg font-semibold text-gray-900 mb-3">Progress by Category</h3>
              <div class="space-y-3">
                <div v-for="(categoryData, categoryName) in groupedItemsByCategory" :key="categoryName"
                  class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div class="flex-1">
                    <h4 class="text-sm font-medium text-gray-900">{{ formatItemType(String(categoryName)) }}</h4>
                    <div class="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div class="bg-primary-500 h-2 rounded-full transition-all duration-300"
                        :style="{ width: `${(categoryData.completed / categoryData.total) * 100}%` }"></div>
                    </div>
                  </div>
                  <div class="ml-3 text-right">
                    <div class="text-sm font-medium text-gray-900">
                      {{ categoryData.completed }}/{{ categoryData.total }}
                    </div>
                    <div class="text-xs text-gray-500">
                      {{ Math.round((categoryData.completed / categoryData.total) * 100) }}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

    <!-- Modals -->
    <EditShoppingItemModal :show="showEditModal" :shopping-item="selectedItem" @close="showEditModal = false"
      @updated="onItemUpdated" />

    <ConfirmDialog :show="showDeleteDialog" title="Delete Shopping Item"
      :message="selectedItem ? `Are you sure you want to delete '${selectedItem.name}'? This action cannot be undone.` : ''"
      confirm-text="Delete" :loading="deletingItem" @confirm="confirmDelete" @cancel="cancelDelete" />
  </div>
</template>
